import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { PRICING, useFounding500, trackFoundingCounterViewed } from "@/lib/billingClient";
import { trackUpgradeCtaClicked } from "@/lib/telemetry";
import { Founding500Counter } from "@/components/Founding500Counter";

/**
 * RC-1 Freemium Lock Modal.
 *
 * Rendered when a Free user attempts a Pro-only action (Calculate on a
 * premium calculator, Polish, Generate Report). Preserves the caller's
 * form state — no data is cleared when the modal is dismissed.
 *
 * Copy is fixed per the freemium spec and must not be changed without a
 * corresponding update to the pricing page.
 */
/** Live Founding 500 counter block — purely presentational, receives the
 * single shared f500 poll state from SubscribeModalBody (its owner) so the
 * CTA buttons below react to the exact same active/inactive state instead
 * of re-deriving it independently. */
function ModalFoundingCounter({ f500 }) {
  return (
    <div className="mb-4" data-testid="modal-founding500">
      <p className="text-sm font-bold text-amber-300 text-center tracking-wide uppercase"
         data-testid="founding500-header">
        Founding 500 — 50% off for life
      </p>
      <Founding500Counter loading={f500.loading} error={f500.error} data={f500.data} compact />
      <p className="text-[11px] text-neutral-400 text-center mt-1.5">
        FOUNDING 500 — 50% off applied automatically at checkout (USD $10/month · USD $100/year after trial).
      </p>
    </div>
  );
}

// SubscribeModalBody — the actual modal contents, split out so its
// useFounding500() poll only ever runs while the modal is genuinely open:
// this component is only ever mounted (by SubscribeModal below) when
// open === true, so its hooks start on mount and stop on unmount exactly
// like the modal's own lifecycle — same guarantee the original
// mounted-only-while-open ModalFoundingCounter had, just shared with the
// CTA buttons too now instead of being local to the counter alone.
function SubscribeModalBody({ onClose, go }) {
  const f500 = useFounding500(60000);
  const foundingActive = Boolean(f500.data?.offer_active && f500.data?.remaining > 0);

  useEffect(() => {
    if (foundingActive) trackFoundingCounterViewed("subscribe_modal", f500.data);
  }, [foundingActive, f500.data]);

  const monthly = foundingActive ? PRICING.founding.monthly : PRICING.pro.monthly;
  const annual  = foundingActive ? PRICING.founding.annual  : PRICING.pro.annual;
  // Founding annual savings ($200 -> $100 = $100) differ from the regular
  // annual savings badge ($200 -> $160... i.e. PRICING.pro.annual.savings);
  // recompute so the badge next to the founding price is never inconsistent
  // with the price shown right beside it.
  const annualSavings = foundingActive
    ? `$${PRICING.pro.annual.amount - PRICING.founding.annual.amount}`
    : PRICING.pro.annual.savings;

  return (
    <div
      data-testid="subscribe-modal-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-modal-title"
    >
      <div
        data-testid="subscribe-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl border border-amber-400/40 bg-neutral-950 shadow-2xl p-6 text-neutral-100"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="text-3xl" aria-hidden="true">🔒</div>
          <button
            type="button"
            data-testid="subscribe-modal-close"
            onClick={onClose}
            className="rounded-full w-8 h-8 flex items-center justify-center text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100 transition"
            aria-label="Close"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <h2 id="subscribe-modal-title" className="text-xl font-semibold mb-2">
          Subscribe to unlock this feature
        </h2>
        <p className="text-neutral-300 text-sm leading-relaxed mb-5">
          This feature is available with a subscription. Unlock all features
          with Pro or Enterprise.
        </p>
        <ModalFoundingCounter f500={f500} />

        <div className="flex flex-col gap-3">
          <button
            type="button"
            data-testid="subscribe-monthly-btn"
            onClick={() => go("pro", "monthly")}
            className="w-full rounded-lg bg-amber-500 hover:bg-amber-400 text-neutral-950 font-semibold py-3 px-4 transition flex items-center justify-center gap-2 flex-wrap"
          >
            <span>Start 7-Day Free Trial —</span>
            {foundingActive && (
              <span data-testid="monthly-was-price" className="line-through opacity-60 font-normal">
                {PRICING.pro.monthly.label}
              </span>
            )}
            <span data-testid="monthly-price">{monthly.label}</span>
          </button>
          <button
            type="button"
            data-testid="subscribe-annual-btn"
            onClick={() => go("pro", "annual")}
            className="w-full rounded-lg bg-neutral-100 hover:bg-white text-neutral-950 font-semibold py-3 px-4 transition flex items-center justify-center gap-2 flex-wrap"
          >
            <span>Start 7-Day Free Trial —</span>
            {foundingActive && (
              <span data-testid="annual-was-price" className="line-through opacity-60 font-normal">
                {PRICING.pro.annual.label}
              </span>
            )}
            <span data-testid="annual-price">{annual.label}</span>
            <span
              data-testid="save-badge"
              className="text-xs rounded-full bg-emerald-500 text-neutral-950 px-2 py-0.5 font-bold"
            >
              Save {annualSavings}
            </span>
          </button>
          <div className="border-t border-neutral-800 my-1" aria-hidden="true" />
          <button
            type="button"
            data-testid="subscribe-enterprise-monthly-btn"
            onClick={() => go("enterprise", "monthly")}
            className="w-full rounded-lg border border-neutral-600 bg-neutral-800 hover:bg-neutral-700 text-white font-semibold py-3 px-4 transition"
          >
            Subscribe Enterprise Monthly — USD $250/month
          </button>
          <button
            type="button"
            data-testid="subscribe-enterprise-annual-btn"
            onClick={() => go("enterprise", "annual")}
            className="w-full rounded-lg border border-neutral-600 bg-neutral-800 hover:bg-neutral-700 text-white font-semibold py-3 px-4 transition flex items-center justify-center gap-2"
          >
            <span>Subscribe Enterprise Annual — USD $2,500/year</span>
            <span
              data-testid="enterprise-save-badge"
              className="text-xs rounded-full bg-emerald-500 text-neutral-950 px-2 py-0.5 font-bold"
            >
              Save $500
            </span>
          </button>
        </div>
        <p className="text-xs text-neutral-500 mt-4">
          Pro plans include a 7-day free trial. Cancel during the trial to avoid the first subscription charge.
        </p>
      </div>
    </div>
  );
}

export default function SubscribeModal({ open, onClose, featureId, source }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (plan, billing) => {
    trackUpgradeCtaClicked(featureId, source || "subscribe_modal");
    onClose?.();
    navigate(`/subscribe?plan=${plan}&billing=${billing}`);
  };

  return <SubscribeModalBody onClose={onClose} go={go} />;
}
